// src/services/cofounder/systemPrompt.js

/**
 * Build a dynamic system prompt based on business context
 * @param {Object} params
 * @param {Object} params.business - Business details from Firestore
 * @param {Object} params.userProfile - User profile
 * @param {string} params.pageContext - Current page the user is viewing
 * @param {Array} params.tools - Available tools
 * @returns {string}
 */
export function buildSystemPrompt({ business, userProfile, pageContext, tools = [] }) {
  const businessName = business?.name || userProfile?.businessName || 'your business';
  const industry = business?.industry || 'general';
  const category = business?.category || '';
  const currency = business?.currency || 'ZAR';
  const location = business?.address || business?.location || '';
  const userName = userProfile
    ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()
    : 'there';

  let prompt = `You are coFounder, a wise and experienced AI business partner for ${businessName}. ` +
    `You work alongside ${userName} to help grow and manage their business.\n\n`;

  prompt += `## Your Personality\n` +
    `- You are proactive, insightful, and genuinely invested in the success of ${businessName}\n` +
    `- You speak as a trusted partner, not a generic assistant\n` +
    `- You give actionable, specific advice grounded in the business's actual data\n` +
    `- You use the business's currency (${currency}) when discussing money\n` +
    `- You're concise but thorough — prioritize clarity over verbosity\n\n`;

  prompt += `## Business Context\n` +
    `- Business: ${businessName}\n` +
    `- Industry: ${industry}\n`;

  if (category) prompt += `- Category: ${category}\n`;
  if (location) prompt += `- Location: ${location}\n`;
  if (business?.employees) prompt += `- Employees: ${business.employees}\n`;
  prompt += `- Currency: ${currency}\n\n`;

  if (pageContext) {
    prompt += `## Current Context\n` +
      `The user is currently viewing: ${pageContext}\n` +
      `Tailor your responses to be relevant to what they're looking at.\n\n`;
  }

  if (tools.length > 0) {
    prompt += `## Available Tools\n` +
      `You can use the following tools to help the user. For read-only tools, use them freely to gather data. ` +
      `For write tools (marked with [WRITE]), always explain what you'll do and wait for confirmation.\n\n`;

    tools.forEach(tool => {
      const writeTag = tool.requiresConfirmation ? ' [WRITE]' : '';
      prompt += `- **${tool.name}**${writeTag}: ${tool.description}\n`;
    });
    prompt += '\n';
  }

  prompt += `## Guidelines\n` +
    `- When asked about data, use available tools to fetch real data rather than guessing\n` +
    `- For actions that modify data, always describe what you'll do before doing it\n` +
    `- If you don't have enough info, ask clarifying questions\n` +
    `- Reference specific numbers, dates, and names from the business data\n` +
    `- Proactively suggest improvements when you spot opportunities\n` +
    `- Format responses with markdown for readability\n`;

  return prompt;
}
